import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import { API_BASE_URL as API_BASE, WS_BASE_URL } from '../../config/apiConfig';

export const analyzeAddress = createAsyncThunk(
  'analysis/analyzeAddress',
  async ({ address, contourInterval, contourTotalLength, skipDem, forceRefresh = false }, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      const user = getState().user.user;
      const userId = user ? user.id : null;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      // Reset step at start
      dispatch(setAnalysisStep(0));
      dispatch(setAnalysisStepText("Connecting to GIS"));

      // Establish WebSocket connection
      let wsUrl = `${WS_BASE_URL}/ws/analysis`;
      if (wsUrl.includes('localhost')) {
          wsUrl = wsUrl.replace('localhost', '127.0.0.1');
      }

      const performHttpRequest = async () => {
          const response = await fetchWithRetry(`${API_BASE}/analyze/address`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              address,
              contour_interval_ft: contourInterval,
              contour_total_length_ft: contourTotalLength,
              skip_dem: skipDem,
              force_refresh: forceRefresh,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            let errorMessage = errorData.detail || 'Analysis failed';
            if (typeof errorMessage === 'object') {
              errorMessage = JSON.stringify(errorMessage);
            }
            try {
              if (typeof errorMessage === 'string' && (errorMessage.includes('{') || errorMessage.includes('['))) {
                let cleanMessage = errorMessage.trim();
                if (cleanMessage.startsWith("'") && cleanMessage.endsWith("'")) {
                  cleanMessage = cleanMessage.substring(1, cleanMessage.length - 1);
                }
                const parsed = JSON.parse(cleanMessage.replace(/'/g, '"'));
                if (parsed.error_type === "NON_RESIDENTIAL_PARCEL") {
                   // Keep the structured JSON error intact for the custom modal
                } else if (parsed.message) {
                  errorMessage = parsed.message;
                  if (parsed.code === 404) {
                     errorMessage = "Property data not found in GIS records. Please verify the address or APN.";
                  }
                } else if (parsed.detail) {
                  errorMessage = parsed.detail;
                }
              }
            } catch (e) {}
            throw new Error(errorMessage);
          }

          return await response.json();
      };

      let intervalId = null;
      const startHttpSimulation = () => {
          let step = 0;
          dispatch(setAnalysisStep(0));
          dispatch(setAnalysisStepText("Connecting to GIS"));
          
          intervalId = setInterval(() => {
              step += 1;
              const stepTexts = [
                  "Connecting to GIS",
                  "Locating Parcel",
                  "Validating Zoning",
                  "Retrieving Geometry",
                  "Fetching Terrain",
                  "Analyzing Slope",
                  "Computing Setbacks",
                  "Calculating MFA/MDA",
                  "Compliance Check",
                  "Finalizing"
              ];
              if (step < stepTexts.length) {
                  dispatch(setAnalysisStep(step));
                  dispatch(setAnalysisStepText(stepTexts[step]));
              }
          }, 3500);
      };

      const runWebSocket = () => {
          return new Promise((resolve, reject) => {
              let socket = null;
              let isOpened = false;
              let hasReceivedMessage = false;

              const connTimeout = setTimeout(() => {
                  if (!isOpened && !hasReceivedMessage) {
                      if (socket) socket.close();
                      reject(new Error('WebSocket connection timed out'));
                  }
              }, 2500);

              try {
                  socket = new WebSocket(wsUrl);
              } catch (err) {
                  clearTimeout(connTimeout);
                  reject(err);
                  return;
              }

              socket.onopen = () => {
                  isOpened = true;
                  clearTimeout(connTimeout);
                  socket.send(JSON.stringify({
                      token,
                      user_id: userId,
                      address,
                      contour_interval_ft: contourInterval,
                      contour_total_length_ft: contourTotalLength,
                      skip_dem: skipDem,
                      force_refresh: forceRefresh,
                  }));
              };

              socket.onmessage = (event) => {
                  hasReceivedMessage = true;
                  try {
                      const message = JSON.parse(event.data);
                      if (message.type === 'progress') {
                          const mappedStep = message.step === 0 ? 0 : Math.min(9, message.step - 1);
                          dispatch(setAnalysisStep(mappedStep));
                          dispatch(setAnalysisStepText(message.status));
                      } else if (message.type === 'complete') {
                          socket.close();
                          if (message.success) {
                              resolve(message);
                          } else {
                              reject(new Error(message.message || 'Analysis failed'));
                          }
                      } else if (message.type === 'error') {
                          socket.close();
                          reject(new Error(message.message || 'Analysis failed'));
                      }
                  } catch (e) {
                      console.error("Failed to parse WebSocket message:", e);
                  }
              };

              socket.onerror = (error) => {
                  clearTimeout(connTimeout);
                  if (socket) socket.close();
                  reject(new Error('WebSocket connection error'));
              };

              socket.onclose = (event) => {
                  clearTimeout(connTimeout);
                  if (event.code !== 1000 && !event.wasClean && !hasReceivedMessage) {
                      reject(new Error('WebSocket closed unexpectedly before message'));
                  }
              };
          });
      };

      try {
          return await runWebSocket();
      } catch (wsError) {
          console.warn("⚠️ WebSocket analysis failed, falling back to HTTP POST:", wsError);
          startHttpSimulation();
          try {
              return await performHttpRequest();
          } finally {
              if (intervalId) clearInterval(intervalId);
          }
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const analyzeMerged = createAsyncThunk(
  'analysis/analyzeMerged',
  async ({ addresses, apns, city, contourInterval, forceRefresh = false }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetchWithRetry(`${API_BASE}/analyze/merged`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          addresses,
          apns,
          city,
          contour_interval_ft: contourInterval,
          skip_dem: true,
          force_refresh: forceRefresh,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = errorData.detail || 'Analysis failed';
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
        try {
          if (typeof errorMessage === 'string' && (errorMessage.includes('{') || errorMessage.includes('['))) {
            let cleanMessage = errorMessage.trim();
            if (cleanMessage.startsWith("'") && cleanMessage.endsWith("'")) {
              cleanMessage = cleanMessage.substring(1, cleanMessage.length - 1);
            }
            const parsed = JSON.parse(cleanMessage.replace(/'/g, '"'));
            if (parsed.error_type === "NON_RESIDENTIAL_PARCEL") {
               // Keep the structured JSON error intact for the custom modal
            } else if (parsed.message) {
              errorMessage = parsed.message;
            } else if (parsed.detail) {
              errorMessage = parsed.detail;
            }
          }
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const analyzeApn = createAsyncThunk(
  'analysis/analyzeApn',
  async ({ apn, contourInterval, contourTotalLength, skipDem, forceRefresh = false, city }, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      const user = getState().user.user;
      const userId = user ? user.id : null;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      // Reset step at start
      dispatch(setAnalysisStep(0));
      dispatch(setAnalysisStepText("Connecting to GIS"));

      // Establish WebSocket connection
      let wsUrl = `${WS_BASE_URL}/ws/analysis`;
      if (wsUrl.includes('localhost')) {
          wsUrl = wsUrl.replace('localhost', '127.0.0.1');
      }

      const performHttpRequest = async () => {
          const response = await fetchWithRetry(`${API_BASE}/analyze/apn`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              apn,
              contour_interval_ft: contourInterval,
              contour_total_length_ft: contourTotalLength,
              skip_dem: skipDem,
              force_refresh: forceRefresh,
              city,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            let errorMessage = errorData.detail || 'Analysis failed';
            if (typeof errorMessage === 'object') {
              errorMessage = JSON.stringify(errorMessage);
            }
            try {
              if (typeof errorMessage === 'string' && (errorMessage.includes('{') || errorMessage.includes('['))) {
                let cleanMessage = errorMessage.trim();
                if (cleanMessage.startsWith("'") && cleanMessage.endsWith("'")) {
                  cleanMessage = cleanMessage.substring(1, cleanMessage.length - 1);
                }
                const parsed = JSON.parse(cleanMessage.replace(/'/g, '"'));
                if (parsed.error_type === "NON_RESIDENTIAL_PARCEL") {
                   // Keep the structured JSON error intact for the custom modal
                } else if (parsed.message) {
                  errorMessage = parsed.message;
                  if (parsed.code === 404) {
                     errorMessage = "Property data not found in GIS records. Please verify the address or APN.";
                  }
                } else if (parsed.detail) {
                  errorMessage = parsed.detail;
                }
              }
            } catch (e) {}
            throw new Error(errorMessage);
          }

          return await response.json();
      };

      let intervalId = null;
      const startHttpSimulation = () => {
          let step = 0;
          dispatch(setAnalysisStep(0));
          dispatch(setAnalysisStepText("Connecting to GIS"));
          
          intervalId = setInterval(() => {
              step += 1;
              const stepTexts = [
                  "Connecting to GIS",
                  "Locating Parcel",
                  "Validating Zoning",
                  "Retrieving Geometry",
                  "Fetching Terrain",
                  "Analyzing Slope",
                  "Computing Setbacks",
                  "Calculating MFA/MDA",
                  "Compliance Check",
                  "Finalizing"
              ];
              if (step < stepTexts.length) {
                  dispatch(setAnalysisStep(step));
                  dispatch(setAnalysisStepText(stepTexts[step]));
              }
          }, 3500);
      };

      const runWebSocket = () => {
          return new Promise((resolve, reject) => {
              let socket = null;
              let isOpened = false;
              let hasReceivedMessage = false;

              const connTimeout = setTimeout(() => {
                  if (!isOpened && !hasReceivedMessage) {
                      if (socket) socket.close();
                      reject(new Error('WebSocket connection timed out'));
                  }
              }, 2500);

              try {
                  socket = new WebSocket(wsUrl);
              } catch (err) {
                  clearTimeout(connTimeout);
                  reject(err);
                  return;
              }

              socket.onopen = () => {
                  isOpened = true;
                  clearTimeout(connTimeout);
                  socket.send(JSON.stringify({
                      token,
                      user_id: userId,
                      apn,
                      city,
                      contour_interval_ft: contourInterval,
                      contour_total_length_ft: contourTotalLength,
                      skip_dem: skipDem,
                      force_refresh: forceRefresh,
                  }));
              };

              socket.onmessage = (event) => {
                  hasReceivedMessage = true;
                  try {
                      const message = JSON.parse(event.data);
                      if (message.type === 'progress') {
                          const mappedStep = message.step === 0 ? 0 : Math.min(9, message.step - 1);
                          dispatch(setAnalysisStep(mappedStep));
                          dispatch(setAnalysisStepText(message.status));
                      } else if (message.type === 'complete') {
                          socket.close();
                          if (message.success) {
                              resolve(message);
                          } else {
                              reject(new Error(message.message || 'Analysis failed'));
                          }
                      } else if (message.type === 'error') {
                          socket.close();
                          reject(new Error(message.message || 'Analysis failed'));
                      }
                  } catch (e) {
                      console.error("Failed to parse WebSocket message:", e);
                  }
              };

              socket.onerror = (error) => {
                  clearTimeout(connTimeout);
                  if (socket) socket.close();
                  reject(new Error('WebSocket connection error'));
              };

              socket.onclose = (event) => {
                  clearTimeout(connTimeout);
                  if (event.code !== 1000 && !event.wasClean && !hasReceivedMessage) {
                      reject(new Error('WebSocket closed unexpectedly before message'));
                  }
              };
          });
      };

      try {
          return await runWebSocket();
      } catch (wsError) {
          console.warn("⚠️ WebSocket analysis failed, falling back to HTTP POST:", wsError);
          startHttpSimulation();
          try {
              return await performHttpRequest();
          } finally {
              if (intervalId) clearInterval(intervalId);
          }
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAnalysisHistory = createAsyncThunk(
  'analysis/fetchHistory',
  async ({ limit = 100, offset = 0 }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetchWithRetry(`${API_BASE}/analyses?limit=${limit}&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analysis history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAnalysisById = createAsyncThunk(
  'analysis/fetchById',
  async (analysisId, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetchWithRetry(`${API_BASE}/analyses/${analysisId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Analysis not found');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const shareProperty = createAsyncThunk(
  'analysis/shareProperty',
  async ({ propertyId, userIds }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetchWithRetry(`${API_BASE}/share/property`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          property_id: propertyId,
          user_ids: userIds,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to share property');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSharedProperties = createAsyncThunk(
  'analysis/fetchSharedProperties',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetchWithRetry(`${API_BASE}/share/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch shared properties');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProposedGfa = createAsyncThunk(
  'analysis/updateProposedGfa',
  async ({ analysisId, proposedGfa }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetch(`${API_BASE}/analyses/${analysisId}/proposed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          proposed_gfa_ft2: proposedGfa,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update target GFA');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProposedScoping = createAsyncThunk(
  'analysis/updateProposedScoping',
  async ({ analysisId, data }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      if (!token) {
        return rejectWithValue('No authentication token available');
      }

      const response = await fetch(`${API_BASE}/analyses/${analysisId}/proposed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update scoping target');
      }

      const resData = await response.json();
      return resData.data; // Return the updated analysis object
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  currentAnalysis: null,
  analysisHistory: [],
  totalAnalyses: 0,
  cityCounts: {},
  isLoading: false,
  isLoadingHistory: false,
  error: null,
  successMessage: null,
  sharedProperties: { sent: [], received: [] },
  analysisStep: 0,
  analysisStepText: 'Connecting to GIS',
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    clearCurrentAnalysis: (state) => {
      state.currentAnalysis = null;
      state.error = null;
      state.successMessage = null;
      state.analysisStep = 0;
      state.analysisStepText = 'Connecting to GIS';
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setCurrentAnalysis: (state, action) => {
      state.currentAnalysis = action.payload;
    },
    updateAnalysisInHistory: (state, action) => {
      const updated = action.payload;
      if (state.currentAnalysis && state.currentAnalysis.data && state.currentAnalysis.data.id === updated.id) {
        state.currentAnalysis.data = {
          ...state.currentAnalysis.data,
          ...updated
        };
      }
      const index = state.analysisHistory.findIndex(a => a.id === updated.id);
      if (index !== -1) {
        state.analysisHistory[index] = {
          ...state.analysisHistory[index],
          ...updated
        };
      }
    },
    setAnalysisStep: (state, action) => {
      state.analysisStep = action.payload;
    },
    setAnalysisStepText: (state, action) => {
      state.analysisStepText = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Analyze Address
      .addCase(analyzeAddress.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(analyzeAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          // Store the complete response data
          state.currentAnalysis = {
            data: action.payload.data,
          };
          state.successMessage = 'Analysis completed successfully';
          // REMOVED: Don't add to history here - let fetchAnalysisHistory handle it
          // The backend already saved it, so just increment the total count
          state.totalAnalyses += 1;
        } else {
          state.error = action.payload.error || 'Analysis failed';
        }
      })
      .addCase(analyzeAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Network error occurred';
      })
      // Analyze APN
      .addCase(analyzeApn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(analyzeApn.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          // Store the complete response data
          state.currentAnalysis = {
            data: action.payload.data,
          };
          state.successMessage = 'Analysis completed successfully';
          // REMOVED: Don't add to history here - let fetchAnalysisHistory handle it
          state.totalAnalyses += 1;
        } else {
          state.error = action.payload.error || 'Analysis failed';
        }
      })
      .addCase(analyzeApn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Network error occurred';
      })
      // Analyze Merged
      .addCase(analyzeMerged.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(analyzeMerged.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.currentAnalysis = {
            data: action.payload.data,
          };
          state.successMessage = 'Analysis completed successfully';
          state.totalAnalyses += 1;
        } else {
          state.error = action.payload.error || 'Analysis failed';
        }
      })
      .addCase(analyzeMerged.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Network error occurred';
      })
      // Fetch Analysis History
      .addCase(fetchAnalysisHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchAnalysisHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        // FIXED: Replace instead of append when offset is 0 (initial load/refresh)
        // For pagination (offset > 0), we append
        const offset = action.meta.arg.offset || 0;
        
        if (offset === 0) {
          // Initial load or refresh - replace the entire history
          state.analysisHistory = action.payload.analyses;
        } else {
          // Pagination - append new results, avoiding duplicates
          const existingIds = new Set(state.analysisHistory.map(a => a.id));
          const newAnalyses = action.payload.analyses.filter(a => !existingIds.has(a.id));
          state.analysisHistory = [...state.analysisHistory, ...newAnalyses];
        }
        
        state.totalAnalyses = action.payload.total;
        state.cityCounts = action.payload.city_counts || {};
      })
      .addCase(fetchAnalysisHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload || 'Failed to load analysis history';
      })
      // Fetch Analysis By ID
      .addCase(fetchAnalysisById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalysisById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAnalysis = {
          data: action.payload,
        };
      })
      .addCase(fetchAnalysisById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load analysis';
      })
      // Fetch Shared Properties
      .addCase(fetchSharedProperties.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchSharedProperties.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.sharedProperties = action.payload; // expects { sent: [], received: [] }
      })
      .addCase(fetchSharedProperties.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload || 'Failed to fetch shared properties';
      })
      // Update Proposed GFA
      .addCase(updateProposedGfa.pending, (state) => {
        // No loading state for simple patch to keep UI snappy
      })
      .addCase(updateProposedGfa.fulfilled, (state, action) => {
        if (state.currentAnalysis && state.currentAnalysis.data) {
          state.currentAnalysis.data.proposed_gfa_ft2 = action.payload.proposed_gfa_ft2;
        }
        // Also update in history if present
        const index = state.analysisHistory.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.analysisHistory[index].proposed_gfa_ft2 = action.payload.proposed_gfa_ft2;
        }
      })
      .addCase(updateProposedGfa.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update target';
      })
      // Update Proposed Scoping
      .addCase(updateProposedScoping.pending, (state) => {
        // Snippy UI
      })
      .addCase(updateProposedScoping.fulfilled, (state, action) => {
        const updatedAnalysis = action.payload;
        if (state.currentAnalysis && state.currentAnalysis.data && state.currentAnalysis.data.id === updatedAnalysis.id) {
          state.currentAnalysis.data = {
            ...state.currentAnalysis.data,
            ...updatedAnalysis
          };
        }
        // Update in history
        const index = state.analysisHistory.findIndex(a => a.id === updatedAnalysis.id);
        if (index !== -1) {
          state.analysisHistory[index] = {
            ...state.analysisHistory[index],
            ...updatedAnalysis
          };
        }
      })
      .addCase(updateProposedScoping.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update scoping targets';
      });
  },
});

export const {
  clearCurrentAnalysis,
  clearError,
  clearSuccessMessage,
  setCurrentAnalysis,
  updateAnalysisInHistory,
  setAnalysisStep,
  setAnalysisStepText,
} = analysisSlice.actions;

export default analysisSlice.reducer;